// Dev-only `PerformanceObserver` that records every `<img>`-initiated
// resource load into the API registry as `kind: 'image'`. Complements
// `api-instrument.ts` — the fetch wrapper can only see images loaded
// via `fetch()` (rare, e.g. blob downloads), so anything rendered by
// Next.js Image, native IMG src loads, picker previews, or background-image CSS
// would be invisible without this observer.
//
// What this captures vs what it doesn't:
//   - Captures: `<img>`, `<picture>`, `next/image` (renders to `<img>`),
//     and (browser-dependent) CSS `background-image: url(...)` loads.
//     The PerformanceResourceTiming API's `initiatorType` is the only
//     reliable signal — we accept `img`, `image`, and `imageset`.
//   - Skips: anything already captured by the fetch wrapper. We do this
//     by ignoring entries whose `initiatorType === 'fetch'` /
//     `xmlhttprequest` — those are tagged by the fetch path with the
//     correct kind already (api / image / other).
//
// Production: gated by the `process.env.NODE_ENV !== 'production'`
// branch in the consumer (`providers/sdk-v2-provider.tsx`), DCE'd in prod.

import { recordApiCall } from './api-registry';

interface ImageGlobals {
  __morfiImageInstrumented?: boolean;
}

const IMAGE_INITIATOR_TYPES: ReadonlySet<string> = new Set<string>(['img', 'image', 'imageset']);

function safeParseUrl(href: string): URL | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(href, window.location.href);
  } catch {
    // SILENT-CATCH: malformed URL — skip the entry.
    return null;
  }
}

function processEntry(entry: PerformanceResourceTiming): void {
  if (!IMAGE_INITIATOR_TYPES.has(entry.initiatorType)) return;
  const parsed = safeParseUrl(entry.name);
  if (parsed === null) return;
  const path = parsed.pathname;
  const query = parsed.search.startsWith('?') ? parsed.search.slice(1) : parsed.search;

  // `responseStatus` is only populated when the response carried the
  // Timing-Allow-Origin header (or the resource is same-origin).
  // Treat 0 as "unknown but non-failing" — the entry exists, so the
  // load reached the network layer at least.
  // ALLOW-UNKNOWN: PerformanceResourceTiming.responseStatus is partially
  // shipped across browsers; the type isn't in the lib.dom.d.ts shipping
  // here yet so we read it via a narrow cast.
  const maybeStatus = (entry as PerformanceResourceTiming & { responseStatus?: number })
    .responseStatus;
  const status = typeof maybeStatus === 'number' && maybeStatus > 0 ? maybeStatus : 0;
  const ok = status === 0 ? true : status >= 200 && status < 400;

  // `transferSize` is 0 for cached / cross-origin-without-TAO entries.
  // Fall back to `encodedBodySize` then `decodedBodySize` so we surface
  // *something* useful in the panel.
  const sizeCandidate = entry.transferSize || entry.encodedBodySize || entry.decodedBodySize;
  const responseBytes =
    typeof sizeCandidate === 'number' && Number.isFinite(sizeCandidate) && sizeCandidate > 0
      ? sizeCandidate
      : null;

  recordApiCall({
    kind: 'image',
    method: 'GET',
    origin: parsed.origin,
    path,
    query,
    fields: '',
    requestBytes: 0,
    responseBytes,
    status,
    ok,
    latencyMs: entry.duration,
    callSite: null,
    bodyDigest: '',
    requestBody: null,
    responseBody: null,
  });
}

/**
 * Patch `PerformanceObserver` once. Idempotent — repeated calls are
 * no-op. No-op outside the browser.
 */
export function installImageInstrumentation(): void {
  if (typeof window === 'undefined') return;
  if (typeof PerformanceObserver === 'undefined') return;
  const g = globalThis as typeof globalThis & ImageGlobals;
  if (g.__morfiImageInstrumented === true) return;
  g.__morfiImageInstrumented = true;

  const observer = new PerformanceObserver((list): void => {
    for (const entry of list.getEntries()) {
      // Cast is safe — `type: 'resource'` only emits PerformanceResourceTiming.
      processEntry(entry as PerformanceResourceTiming);
    }
  });

  try {
    // `buffered: true` flushes any image entries that already loaded
    // before the observer attached (e.g. above-the-fold imagery from
    // the initial paint). Without this, the panel misses the first
    // wave of loads on hard refresh.
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    // SILENT-CATCH: a browser that doesn't support `type:` syntax
    // falls through; we'd need entryTypes:['resource'] which doesn't
    // accept buffered. Acceptable to skip in those environments.
  }
}
